import { CarouselContext } from 'pure-react-carousel'
import { stringifyUrl } from 'query-string'
import { ReactElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import {
  Button,
  Dropdown as SizeSelector,
  TypeStyleHeadlineMicro,
  ViewportSizes,
  useIsViewport,
} from '@syconium/little-miss-figgy'

import { gql } from '../../../../__generated__/graphql/catalog/gql'
import { UpsellTileVariantFragment } from '../../../../__generated__/graphql/catalog/graphql'
import { NextLink } from '../../../../app/_components/navigation/NextLink'
import {
  PricingRange,
  derivePricingForVariant,
} from '../../../../app/_components/pricing/PricingRange.client'
import { useLocalization } from '../../../../app/_providers/LocalizationProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { CartContainer } from '../../../../brunswick/containers/cart'
import { trackEvent } from '../../../../lib/analytics'
import { convertApiDataToNewCartItem } from '../../../containers/cart/utils'

import {
  AddButtonWrapper,
  Controls,
  DetailText,
  Details,
  PriceAndColor,
  ProductName,
  SizeSelectorWrapper,
  UpsellTile as StyledTile,
  Thumbnail,
  TileOverview,
} from './styles'

const SIZE_SELECTOR_OPTION_HEIGHT: number = 29 // px
const SIZE_SELECTOR_BUTTON_HEIGHT: number = 24 // px

gql(`
  fragment UpsellTileSelectedVariant on ProductCatalogVariant {
    __typename
    id
    urlPath
    shopifyId
    shopifyProductId
    sku
    availability
    price {
      amount
      currencyCode
    }
    compareAtPrice {
      amount
    }
    size {
      id
      displayValue
    }
    fit {
      id
      displayValue
    }
  }
`)

gql(`
  fragment UpsellTileVariant on ProductCatalogVariant {
    ...UpsellTileSelectedVariant
    group {
      __typename
      id
      title
      urlPath
      siteWidePromoEligible
      finalSale
      primaryCategory {
        key
        analyticsId
      }
      images(first: 1) {
        url
      }
      color {
        displayValue
      }
      variants {
        ...UpsellTileSelectedVariant
      }
      product {
        __typename
        id
        handle
        options {
          sizes {
            size {
              id
              displayValue
            }
            outOfStock
          }
        }
      }
    }
  }
`)

export const UpsellTile = ({
  nostoPlacementId,
  variant: initialVariant,
  i,
}: {
  nostoPlacementId?: string
  variant: UpsellTileVariantFragment
  i: number
}): ReactElement => {
  const variantGroup = initialVariant.group
  const { addItems } = CartContainer.useContainer()
  const { cart: miniCartCopy } = useTranslation()
  const { locale } = useLocalization()

  const [windowInnerHeight, setWindowInnerHeight] = useState<number>(() => {
    return typeof window !== 'undefined' ? window.innerHeight : 0
  })
  const [selectedSizeDisplayString, setSelectedSizeDisplayString] = useState('')
  const sizesDisplayStrings = useMemo(() => {
    const sizes = variantGroup.product.options.sizes
    if (sizes.length === 0) {
      return ['O/S']
    } else {
      return sizes.map(size => {
        if (size.outOfStock) {
          return `${size.size.displayValue} ${miniCartCopy.soldOutLabel}`
        } else {
          return size.size.displayValue
        }
      })
    }
  }, [miniCartCopy.soldOutLabel, variantGroup.product.options.sizes])
  const selectedSizeKey = useMemo(() => {
    const sizes = variantGroup.product.options.sizes
    return (
      sizes.find(size => size.size.displayValue === selectedSizeDisplayString)?.size.id ??
      initialVariant.size?.id ??
      ''
    )
  }, [initialVariant.size?.id, selectedSizeDisplayString, variantGroup.product.options.sizes])

  const [sizeSelectorYOffset, setSizeSelectorYOffset] = useState<number>(0)
  // This is to allow for flexibility wrt sizes available for various products. An individual cell height
  // (in the size-selector dropdown) is ~29px, so we multiply that number by the number of items in the sizes array.
  // This gives us a reasonable height that we can use to determine if we should open the dropdown
  // upwards or downwards.
  const [sizeSelectorHeight, setSizeSelectorHeight] = useState<number>(
    SIZE_SELECTOR_OPTION_HEIGHT * sizesDisplayStrings.length + SIZE_SELECTOR_BUTTON_HEIGHT
  )
  const sizeSelectorWrapperRef = useRef<HTMLDivElement | null>(null)
  const syncDimensions = () => {
    setWindowInnerHeight(typeof window !== 'undefined' ? window.innerHeight : 0)
    setSizeSelectorYOffset(sizeSelectorWrapperRef.current?.getBoundingClientRect().y ?? 0)
  }

  const carouselContext = useContext(CarouselContext)
  const [currentSlide, setCurrentSlide] = useState(carouselContext.state.currentSlide)

  useEffect(() => {
    function onChange() {
      setCurrentSlide(carouselContext.state.currentSlide)
    }
    carouselContext.subscribe(onChange)
    return () => carouselContext.unsubscribe(onChange)
  }, [carouselContext])

  const sizeSelectorOpeningDirection: 'up' | 'down' =
    useIsViewport(ViewportSizes.large) ||
    windowInnerHeight < sizeSelectorHeight + sizeSelectorYOffset
      ? 'up'
      : 'down'
  useEffect(() => {
    setSizeSelectorHeight(
      SIZE_SELECTOR_BUTTON_HEIGHT +
        // 12px, here, is an allowance for border/margin additions
        (sizeSelectorOpeningDirection === 'up' ? 12 : 0) +
        SIZE_SELECTOR_OPTION_HEIGHT * sizesDisplayStrings.length
    )
  }, [sizeSelectorOpeningDirection, sizesDisplayStrings.length])

  useEffect(() => {
    syncDimensions()
    window.addEventListener('resize', syncDimensions, { passive: true })
    window.addEventListener('scroll', syncDimensions, { passive: true })

    return () => {
      window.removeEventListener('resize', syncDimensions)
      window.removeEventListener('scroll', syncDimensions)
    }
  }, [])

  const { ref: setInViewRef, inView } = useInView({
    threshold: 0,
    triggerOnce: true,
    fallbackInView: true,
  })
  const onSizeSelectorWrapperElementChange = useCallback(
    (newElement: HTMLDivElement) => {
      sizeSelectorWrapperRef.current = newElement
      setInViewRef(newElement)
    },
    [setInViewRef]
  )

  useEffect(() => {
    setSizeSelectorYOffset(sizeSelectorWrapperRef?.current?.getBoundingClientRect().y ?? 0)
  }, [inView])

  const onUserSizeSelectionChange = useMemo(() => {
    return (size: string) => {
      setSelectedSizeDisplayString(size)
    }
  }, [])

  const selectedVariant = useMemo(() => {
    return (
      variantGroup.variants.find(v => {
        return v.size?.id === selectedSizeKey && v.fit?.id === initialVariant.fit?.id
      }) ?? initialVariant
    )
  }, [initialVariant, selectedSizeKey, variantGroup.variants])

  const productHref = stringifyUrl({
    url: variantGroup.urlPath,
    query: { nosto: nostoPlacementId },
  })

  const pricing = useMemo(() => {
    return derivePricingForVariant(selectedVariant)
  }, [selectedVariant])

  const imageUrl = variantGroup.images?.[0]?.url
  const colorDisplayValue = variantGroup.color?.displayValue

  return (
    <StyledTile>
      <NextLink
        href={productHref}
        {...trackEvent({
          category: 'minicart upsell',
          action: 'click product image',
        })}
        tabIndex={currentSlide === i ? 0 : -1}
      >
        <TileOverview>
          {imageUrl ? <Thumbnail imgUrl={imageUrl} /> : null}
          <Details>
            <ProductName>{variantGroup.title}</ProductName>
            <PriceAndColor>
              {pricing ? (
                <TypeStyleHeadlineMicro>
                  <PricingRange {...pricing} percentageOff={null} upToPercentageOff={null} />
                </TypeStyleHeadlineMicro>
              ) : null}
              {colorDisplayValue ? <DetailText>{colorDisplayValue}</DetailText> : null}
            </PriceAndColor>
          </Details>
        </TileOverview>
      </NextLink>
      <Controls>
        {Boolean(sizesDisplayStrings.length) && (
          <SizeSelectorWrapper
            openingDirection={sizeSelectorOpeningDirection}
            dropdownHeight={sizeSelectorHeight}
            data-testid='FIGS__Upsells__SizeSelectorWrapper'
            ref={onSizeSelectorWrapperElementChange}
            {...trackEvent({
              category: 'minicart upsell',
              action: 'select size',
            })}
          >
            <SizeSelector
              onChange={x => onUserSizeSelectionChange(x)}
              options={sizesDisplayStrings}
              selection={selectedSizeDisplayString}
              placeholderText={miniCartCopy.selectSize}
              tabIndex={currentSlide === i ? 0 : -1}
              isScrollable
              {...trackEvent({
                category: 'minicart upsell',
                action: 'click size selector',
                value: selectedSizeKey ?? '',
              })}
            />
          </SizeSelectorWrapper>
        )}

        <AddButtonWrapper>
          <Button
            allCaps
            block
            data-testid='FIGS__Upsells__AddButton'
            disabled={!selectedVariant || selectedVariant.availability !== 'AVAILABLE'}
            onClick={() => {
              addItems([
                convertApiDataToNewCartItem({
                  locale,
                  product: variantGroup.product,
                  variant: selectedVariant,
                  variantGroup,
                }),
              ])
            }}
            size='large'
            {...trackEvent({
              category: 'minicart upsell',
              action: 'click add to bag',
              label: 'Add to Bag',
            })}
            tabIndex={currentSlide === i ? 0 : -1}
          >
            {miniCartCopy.add}
          </Button>
        </AddButtonWrapper>
      </Controls>
    </StyledTile>
  )
}
